export abstract class StringFormat {
  /**
   * Cleans a string by normalizing and removing diacritical marks
   *
   * @param value
   * @returns
   */
  private static cleanString(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/\u0300-\u036f/g, "");
  }

  /**
   * Formats a string to its slug value
   *
   * @param value
   * @param options.preserveColon keep ":" (used by backend to disambiguate make/model/version)
   * @returns
   */
  static formatSlug(
    value: string,
    options?: { preserveColon?: boolean },
  ): string {
    if (!value || typeof value !== "string") {
      return "";
    }

    const disallowedCharsPattern = options?.preserveColon
      ? /[^a-z0-9\s:-]/g
      : /[^a-z0-9\s-]/g;

    return StringFormat.cleanString(value)
      .replace(disallowedCharsPattern, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  }

  /**
   * Compacts a string by removing spaces and invalid characters
   *
   * @param value
   * @returns
   */
  static compactString(value = "", options?: { lowerCase: boolean }): string {
    if (!value || typeof value !== "string") {
      return "";
    }

    const compactedString = StringFormat.cleanString(value).replace(
      /[^a-z0-9]/g,
      ""
    );

    return options?.lowerCase
      ? compactedString.toLocaleLowerCase()
      : compactedString;
  }

  /**
   * Capitalize words in a string
   *
   * @param value
   * @returns
   */
  static capitalizeWords(
    value: string,
    options?: { onlyFirstWord?: boolean }
  ): string {
    if (!value || typeof value !== "string") return "";
    return value
      .toLocaleLowerCase()
      .split(" ")
      .map((word, index) =>
        options?.onlyFirstWord && index > 0
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ");
  }

  /**
   * Truncate a string
   *
   * @param text
   * @param maxLength
   * @returns
   */
  static truncateString(text: string, maxLength: number): string {
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  }

  static toKebabCase(text: string): string {
    return text.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  }

  static parseSlug(value: string): string {
    if (!value || typeof value !== "string") {
      return "";
    }

    return value.replace(/-/g, " ").replace(/\s+/g, " ").trim();
  }
}
